import { useCallback, useEffect, useRef, useState } from 'react';
import TRTC from 'trtc-sdk-v5';
import { VirtualBackground } from 'trtc-sdk-v5/plugins/video-effect/virtual-background';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function tileKey(userId, streamType) {
  return `${userId}_${streamType}`;
}

const VIDEO_PROFILE = '720p';
const SpeechRecognitionCtor = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const INACTIVITY_WARNING_MS = 4 * 60 * 1000;
const INACTIVITY_END_MS = 5 * 60 * 1000;
const VOLUME_ACTIVITY_THRESHOLD = 5;
const CMD_TRANSCRIPT = 1;
const CMD_CONTROL = 2;
const CMD_CAPTION = 3;
const MAX_CAPTION_HISTORY = 200;

export function useTRTC() {
  const trtcRef = useRef(null);
  const tokenRef = useRef(null);
  const startedVideoKeys = useRef(new Set());
  const pendingResumeRef = useRef([]);
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const isHostRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);
  const [joined, setJoined] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState('DISCONNECTED');
  const [reconnecting, setReconnecting] = useState(false);
  const [remoteTiles, setRemoteTiles] = useState([]);
  const [networkQuality, setNetworkQuality] = useState({});
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);
  const [inactivityWarningAt, setInactivityWarningAt] = useState(0);
  const [autoEndSignal, setAutoEndSignal] = useState(null);
  const [bgBlurOn, setBgBlurOn] = useState(false);
  const [bgBlurSupported, setBgBlurSupported] = useState(true);
  const [caption, setCaption] = useState(null);
  const [captionHistory, setCaptionHistory] = useState([]);
  const [captionsFeatureEnabled, setCaptionsFeatureEnabled] = useState(false);
  const captionsFeatureEnabledRef = useRef(false);
  const [captionsOn, setCaptionsOn] = useState(() => {
    try {
      const saved = localStorage.getItem('app_captions_on');
      return saved === null ? true : saved === 'true';
    } catch (e) {
      return true;
    }
  });
  const [captionPanelOpen, setCaptionPanelOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('app_caption_panel_open');
      return saved === null ? true : saved === 'true';
    } catch (e) {
      return true;
    }
  });

  const toggleCaptionsOn = useCallback(() => {
    setCaptionsOn((old) => {
      const next = !old;
      try {
        localStorage.setItem('app_captions_on', String(next));
      } catch (e) {
        // localStorage unavailable, in-memory toggle still works
      }
      return next;
    });
  }, []);

  const toggleCaptionPanel = useCallback(() => {
    setCaptionPanelOpen((old) => {
      const next = !old;
      try {
        localStorage.setItem('app_caption_panel_open', String(next));
      } catch (e) {
        // localStorage unavailable, in-memory toggle still works
      }
      return next;
    });
  }, []);

  const showCaption = useCallback((cap) => {
    setCaption((prev) => {
      // Keep the previous final caption visible when the same speaker starts a new phrase (pending "...").
      // Only clear when a different speaker begins, or when the same speaker's new translation is finalized.
      if (cap?.pending && prev && !prev.pending && prev.speaker === cap.speaker) {
        return prev;
      }
      return cap;
    });
  }, []);

  const addCaptionToHistory = useCallback((cap) => {
    setCaptionHistory((old) => [...old, cap].slice(-MAX_CAPTION_HISTORY));
  }, []);

  const translateAndBroadcast = useCallback(
    async (speaker, text, targetLang) => {
      try {
        const { data } = await axios.post(`${API}/captions/translate`, { text, target_lang: targetLang });
        const cap = { speaker, original: text, translated: data.translated, ts: Date.now(), pending: false };
        showCaption(cap);
        addCaptionToHistory(cap);
        try {
          const payload = new TextEncoder().encode(JSON.stringify(cap)).buffer;
          trtcRef.current?.sendCustomMessage({ cmdId: CMD_CAPTION, data: payload });
        } catch (e) {
          // caption broadcast failed, local caption still shown
        }
      } catch (e) {
        // translation failed, skip this caption silently
      }
    },
    [showCaption, addCaptionToHistory]
  );

  useEffect(() => {
    try {
      setBgBlurSupported(VirtualBackground.isSupported());
    } catch (e) {
      setBgBlurSupported(false);
    }
  }, []);

  useEffect(() => {
    isHostRef.current = isHost;
  }, [isHost]);

  const toggleCaptionsFeature = useCallback(async (roomId) => {
    const next = !captionsFeatureEnabledRef.current;
    try {
      await axios.patch(`${API}/rooms/${roomId}/captions`, { enabled: next });
      setCaptionsFeatureEnabled(next);
      try {
        const data = new TextEncoder().encode(JSON.stringify({ type: 'captions_feature', enabled: next })).buffer;
        trtcRef.current?.sendCustomMessage({ cmdId: CMD_CONTROL, data });
      } catch (e) {
        // broadcast failed, other participants will pick up the state on their next join
      }
    } catch (e) {
      // toggle request failed, keep previous state
    }
  }, []);

  useEffect(() => {
    captionsFeatureEnabledRef.current = captionsFeatureEnabled;
  }, [captionsFeatureEnabled]);

  const startTranscription = useCallback((speakerName, appLang) => {
    if (!SpeechRecognitionCtor) return;
    const trtc = trtcRef.current;
    shouldListenRef.current = true;
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = appLang === 'en' ? 'en-US' : 'zh-CN';
    const targetLang = appLang === 'en' ? 'zh' : 'en';
    let lastInterimText = '';
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript.trim();
        if (!text) continue;

        if (!result.isFinal) {
          if (text !== lastInterimText && captionsFeatureEnabledRef.current) {
            lastInterimText = text;
            const cap = { speaker: speakerName, pending: true, ts: Date.now() };
            showCaption(cap);
            try {
              const payload = new TextEncoder().encode(JSON.stringify(cap)).buffer;
              trtc?.sendCustomMessage({ cmdId: CMD_CAPTION, data: payload });
            } catch (e) {
              // pending caption broadcast failed, local preview still shown
            }
          }
          continue;
        }

        lastInterimText = '';
        const finalText = text.slice(0, 300);
        const entry = { speaker: speakerName, text: finalText, ts: Date.now() };
        setTranscript((old) => [...old, entry]);
        try {
          const data = new TextEncoder().encode(JSON.stringify(entry)).buffer;
          trtc?.sendCustomMessage({ cmdId: CMD_TRANSCRIPT, data });
        } catch (e) {
          // custom message send failed, keep local copy only
        }
        if (captionsFeatureEnabledRef.current) {
          translateAndBroadcast(speakerName, finalText, targetLang);
        }
      }
    };
    recognition.onerror = () => {};
    recognition.onend = () => {
      if (shouldListenRef.current) {
        try {
          recognition.start();
        } catch (e) {
          // already started
        }
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      // mic not available for STT
    }
  }, []);

  const stopTranscription = useCallback(() => {
    shouldListenRef.current = false;
    recognitionRef.current?.stop?.();
    recognitionRef.current = null;
  }, []);

  const join = useCallback(async ({ roomId, userId, name, localVideoEl, title, appLang }) => {
    setConnecting(true);
    setError(null);
    try {
      await TRTC.isSupported();
      const { data: token } = await axios.get(`${API}/trtc/token`, { params: { user_id: userId } });
      tokenRef.current = token;
      const { data: joinInfo } = await axios.post(`${API}/rooms/${roomId}/join`, { user_id: userId, name, title });
      setIsHost(!!joinInfo?.isHost);
      setCaptionsFeatureEnabled(!!joinInfo?.captionsEnabled);

      const trtc = TRTC.create();
      trtcRef.current = trtc;
      try {
        trtc.use({ plugin: VirtualBackground, assetsPath: '/trtc-assets/' });
      } catch (e) {
        // plugin registration failed, blur toggle will no-op safely
      }

      trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, ({ userId: uid, streamType }) => {
        const key = tileKey(uid, streamType);
        setRemoteTiles((old) => {
          const exists = old.find((t) => t.key === key);
          if (exists) return old.map((t) => (t.key === key ? { ...t, hasVideo: true } : t));
          return [...old, { key, userId: uid, streamType, hasVideo: true, hasAudio: false }];
        });
      });
      trtc.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, ({ userId: uid, streamType }) => {
        const key = tileKey(uid, streamType);
        setRemoteTiles((old) => old.filter((t) => t.key !== key));
      });
      trtc.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, ({ userId: uid }) => {
        const key = tileKey(uid, 'main');
        setRemoteTiles((old) => {
          const exists = old.find((t) => t.key === key);
          if (exists) return old.map((t) => (t.key === key ? { ...t, hasAudio: true } : t));
          return [...old, { key, userId: uid, streamType: 'main', hasVideo: false, hasAudio: true }];
        });
      });
      trtc.on(TRTC.EVENT.REMOTE_AUDIO_UNAVAILABLE, ({ userId: uid }) => {
        const key = tileKey(uid, 'main');
        setRemoteTiles((old) => old.map((t) => (t.key === key ? { ...t, hasAudio: false } : t)));
      });
      trtc.on(TRTC.EVENT.REMOTE_USER_EXIT, ({ userId: uid }) => {
        setRemoteTiles((old) => old.filter((t) => t.userId !== uid));
        setNetworkQuality((old) => {
          const next = { ...old };
          delete next[uid];
          return next;
        });
      });
      trtc.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, (event) => {
        setConnectionState(event.state);
        setReconnecting(event.state === 'CONNECTING' && !!event.isReconnecting);
      });
      trtc.on(TRTC.EVENT.NETWORK_QUALITY, (event) => {
        setNetworkQuality((old) => ({
          ...old,
          local: {
            uplink: event.uplinkNetworkQuality,
            downlink: event.downlinkNetworkQuality,
            uplinkRTT: event.uplinkRTT,
            uplinkLoss: event.uplinkLoss,
            downlinkRTT: event.downlinkRTT,
            downlinkLoss: event.downlinkLoss,
          },
          ...(event.remoteNetworkQuality || {}),
        }));
      });
      trtc.on(TRTC.EVENT.SCREEN_SHARE_STOPPED, () => {
        setScreenSharing(false);
      });
      trtc.on(TRTC.EVENT.AUTOPLAY_FAILED, (event) => {
        setAutoplayBlocked(true);
        pendingResumeRef.current.push(event);
      });
      trtc.on(TRTC.EVENT.CUSTOM_MESSAGE, (event) => {
        if (event.cmdId === CMD_CONTROL) {
          try {
            const msg = JSON.parse(new TextDecoder().decode(event.data));
            if (msg.type === 'meeting_warning') setInactivityWarningAt(Date.now());
            if (msg.type === 'meeting_ended') setAutoEndSignal('inactivity');
            if (msg.type === 'captions_feature') setCaptionsFeatureEnabled(!!msg.enabled);
          } catch (e) {
            // ignore malformed control message
          }
          return;
        }
        if (event.cmdId === CMD_CAPTION) {
          try {
            const cap = JSON.parse(new TextDecoder().decode(event.data));
            showCaption(cap);
            if (!cap.pending) addCaptionToHistory(cap);
          } catch (e) {
            // ignore malformed caption message
          }
          return;
        }
        try {
          const text = new TextDecoder().decode(event.data);
          const entry = JSON.parse(text);
          setTranscript((old) => [...old, entry]);
        } catch (e) {
          // ignore malformed custom message
        }
      });
      trtc.on(TRTC.EVENT.AUDIO_VOLUME, (event) => {
        const speaking = (event.result || []).some((r) => r.volume > VOLUME_ACTIVITY_THRESHOLD);
        if (speaking) {
          lastActivityRef.current = Date.now();
          warnedRef.current = false;
        }
      });
      let videoPublishRetries = 0;
      trtc.on(TRTC.EVENT.PUBLISH_STATE_CHANGED, (event) => {
        if (event.mediaType === 'video' && event.state === 'stopped' && event.reason === 'error' && videoPublishRetries < 2) {
          videoPublishRetries += 1;
          setTimeout(() => {
            trtc.updateLocalVideo({ publish: true }).catch(() => {});
          }, 1500 * videoPublishRetries);
        }
      });
      trtc.on(TRTC.EVENT.ERROR, (err) => {
        if (err?.extraCode === 5203 || /H\.?264/i.test(err?.message || '')) {
          setError('您的浏览器/设备不支持发送视频（H.264编码不可用），对方可能无法看到您的画面。建议使用最新版 Chrome 或 Edge 桌面浏览器。');
        } else {
          setError(err?.message || 'A TRTC error occurred');
        }
      });

      await trtc.enterRoom({
        strRoomId: roomId,
        scene: 'rtc',
        sdkAppId: token.sdkAppId,
        userId: token.userId,
        userSig: token.userSig,
      });
      await trtc.startLocalVideo({ view: localVideoEl, option: { profile: VIDEO_PROFILE } });
      await trtc.startLocalAudio();
      trtc.enableAudioVolumeEvaluation(1000);
      lastActivityRef.current = Date.now();
      warnedRef.current = false;

      setJoined(true);
      setConnectionState('CONNECTED');
      startTranscription(name, appLang);
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to join the call';
      setError(message);
      throw new Error(message);
    } finally {
      setConnecting(false);
    }
  }, []);

  const leave = useCallback(async ({ roomId, userId }) => {
    stopTranscription();
    const trtc = trtcRef.current;
    if (trtc) {
      try {
        await trtc.exitRoom();
      } catch (e) {
        // already disconnected
      }
      trtc.destroy?.();
      trtcRef.current = null;
    }
    if (roomId && userId) {
      axios.post(`${API}/rooms/${roomId}/leave`, { user_id: userId }).catch(() => {});
    }
    setJoined(false);
    setRemoteTiles([]);
    setNetworkQuality({});
    setConnectionState('DISCONNECTED');
    setScreenSharing(false);
    startedVideoKeys.current.clear();
    pendingResumeRef.current = [];
    setAutoplayBlocked(false);
    setAutoEndSignal(null);
    setBgBlurOn(false);
    setCaption(null);
  }, []);

  useEffect(() => {
    if (!joined) return;
    const interval = setInterval(() => {
      if (!isHostRef.current) return;
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= INACTIVITY_END_MS) {
        try {
          const data = new TextEncoder().encode(JSON.stringify({ type: 'meeting_ended' })).buffer;
          trtcRef.current?.sendCustomMessage({ cmdId: CMD_CONTROL, data });
        } catch (e) {
          // ignore send failure, host still ends locally
        }
        setAutoEndSignal('inactivity');
      } else if (idleMs >= INACTIVITY_WARNING_MS && !warnedRef.current) {
        warnedRef.current = true;
        try {
          const data = new TextEncoder().encode(JSON.stringify({ type: 'meeting_warning' })).buffer;
          trtcRef.current?.sendCustomMessage({ cmdId: CMD_CONTROL, data });
        } catch (e) {
          // ignore send failure, host still warns locally
        }
        setInactivityWarningAt(Date.now());
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [joined]);

  useEffect(() => {
    const trtc = trtcRef.current;
    if (!trtc) return;
    remoteTiles.forEach((t) => {
      if (t.hasVideo && !startedVideoKeys.current.has(t.key)) {
        startedVideoKeys.current.add(t.key);
        trtc.startRemoteVideo({ userId: t.userId, streamType: t.streamType, view: t.key }).catch(() => {
          startedVideoKeys.current.delete(t.key);
        });
      }
      if (!t.hasVideo) {
        startedVideoKeys.current.delete(t.key);
      }
    });
  }, [remoteTiles]);

  const toggleMic = useCallback(async () => {
    const trtc = trtcRef.current;
    if (!trtc) return;
    const next = !muted;
    await trtc.updateLocalAudio({ mute: next });
    setMuted(next);
  }, [muted]);

  const toggleCamera = useCallback(
    async (localVideoEl) => {
      const trtc = trtcRef.current;
      if (!trtc) return;
      const next = !cameraOff;
      if (next) {
        await trtc.stopLocalVideo();
      } else {
        await trtc.startLocalVideo({ view: localVideoEl, option: { profile: VIDEO_PROFILE } });
      }
      setCameraOff(next);
    },
    [cameraOff]
  );

  const toggleScreenShare = useCallback(async () => {
    const trtc = trtcRef.current;
    if (!trtc) return;
    try {
      if (screenSharing) {
        await trtc.stopScreenShare();
        setScreenSharing(false);
      } else {
        await trtc.startScreenShare();
        setScreenSharing(true);
      }
    } catch (err) {
      setScreenSharing(false);
    }
  }, [screenSharing]);

  const toggleBgBlur = useCallback(async () => {
    const trtc = trtcRef.current;
    if (!trtc || !tokenRef.current) return;
    try {
      if (bgBlurOn) {
        await trtc.stopPlugin('VirtualBackground');
        setBgBlurOn(false);
      } else {
        await trtc.startPlugin('VirtualBackground', {
          sdkAppId: tokenRef.current.sdkAppId,
          userId: tokenRef.current.userId,
          userSig: tokenRef.current.userSig,
          type: 'blur',
          blurLevel: 2,
        });
        setBgBlurOn(true);
      }
    } catch (e) {
      setBgBlurOn(false);
    }
  }, [bgBlurOn]);

  const resumePlayback = useCallback(() => {
    pendingResumeRef.current.forEach((event) => event.resume?.().catch?.(() => {}));
    pendingResumeRef.current = [];
    setAutoplayBlocked(false);
  }, []);

  useEffect(() => {
    return () => {
      const trtc = trtcRef.current;
      if (trtc) {
        trtc.exitRoom().catch(() => {});
        trtc.destroy?.();
      }
    };
  }, []);

  return {
    joined,
    connecting,
    connectionState,
    reconnecting,
    remoteTiles,
    networkQuality,
    muted,
    cameraOff,
    screenSharing,
    autoplayBlocked,
    resumePlayback,
    isHost,
    transcript,
    error,
    inactivityWarningAt,
    autoEndSignal,
    bgBlurOn,
    bgBlurSupported,
    caption,
    captionHistory,
    captionsOn,
    toggleCaptionsOn,
    captionPanelOpen,
    toggleCaptionPanel,
    captionsFeatureEnabled,
    toggleCaptionsFeature,
    join,
    leave,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    toggleBgBlur,
  };
}
